import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';

import type { IconAvatar } from '@evc/web-components';
import { AvatarComponent, MenuItemComponent } from '@evc/web-components';

import { AuthService } from '../../clients/core/auth/service/auth.service';
import { OrganizationsService } from '../../clients/core/organizations/organizations.service';
import { UserService } from '../../clients/core/user/user.service';
import { I18nPlatformService } from '../../features/i18n/i18n.service';
import type { Guid } from '../../features/types/api-objects.type';

@Component({
  selector: 'evc-auth-organization-selection-modal',
  imports: [AvatarComponent, MenuItemComponent],
  templateUrl: './auth-organization-selection-modal.html',
  styleUrl: './auth-organization-selection-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthOrganizationSelectionModalComponent {
  readonly #userService = inject(UserService);
  readonly #organizationsService = inject(OrganizationsService);
  readonly #i18n = inject(I18nPlatformService);
  readonly #authService = inject(AuthService);

  readonly chooseOrgLabel = this.#i18n.tSignal('auth-organization-selection-modal.choose_org');
  readonly useAnotherAccountLabel = this.#i18n.tSignal(
    'auth-organization-selection-modal.use_another_account',
  );
  readonly logoutLabel = this.#i18n.tSignal('auth-organization-selection-modal.logout');

  readonly orgSelected = output<Guid>();
  readonly useAnotherAccount = output();

  readonly userProfile = this.#userService.profile.bind(this.#userService);

  readonly welcome = computed(() => {
    const displayName = this.#userService.profile()?.givenName ?? '';

    return this.#i18n.t('auth-organization-selection-modal.welcome', { displayName });
  });

  readonly organizations = this.#organizationsService.selectionItems;

  readonly useAnotherAccountAvatar: IconAvatar = {
    type: 'icon',
    icon: 'profile',
    color: 'var(--content-accent)',
  };

  readonly logoutAvatar: IconAvatar = {
    type: 'icon',
    icon: 'log-out',
    color: 'var(--content-accent)',
  };

  readonly logout = this.#authService.logout.bind(this.#authService);
}
